/**
 * WordPress Dependencies
 */
import { RichText, useBlockProps } from '@wordpress/block-editor';
import { Fragment, useEffect, RawHTML } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * External Dependencies
 */
import classNames from 'classnames';

/**
 * Internal Dependencies
 */
import { handleUniqueId } from '../../helper';
// inspector controls
import Inspector from './inspector';
import ICONS from '../../controls/icon-picker-control/icons';

// Dynamic Style
import DynamicStyle from './dynamic-style';

// Helper function to get prefix based on type
const getPrefix = (index, type) => {
    switch (type) {
        case 'numeric':
            return `${index + 1}.`;
        case 'question':
            return `Q${index + 1}.`;
        case 'alpha-upper':
            return `${String.fromCharCode(65 + index)}.`;
        case 'alpha-lower':
            return `${String.fromCharCode(97 + index)}.`;
        case 'roman-upper':
            return `${toRoman(index + 1)}.`;
        case 'roman-lower':
            return `${toRoman(index + 1).toLowerCase()}.`;
        case 'bracket-numeric':
            return `[${index + 1}]`;
        case 'bracket-question':
            return `[Q${index + 1}]`;
        default:
            return '';
    }
};

// Helper function to convert to Roman numerals
const toRoman = num => {
    const romanNumerals = [
        { value: 10, numeral: 'X' },
        { value: 9, numeral: 'IX' },
        { value: 5, numeral: 'V' },
        { value: 4, numeral: 'IV' },
        { value: 1, numeral: 'I' }
    ];

    let result = '';
    for (const { value, numeral } of romanNumerals) {
        while (num >= value) {
            result += numeral;
            num -= value;
        }
    }
    return result;
};

// block edit function
const Edit = props => {
    const { attributes, setAttributes, clientId, isSelected } = props;
    const {
        uniqueId,
        items,
        active,
        iconPosition,
        enablePrefix,
        titleTag,
        sourceType,
        customIcon,
        icon,
        showIcon,
        // New attributes for auto numbering
        enableAutoNumbering = false,
        numberingType = 'numeric',
        numberingPosition = 'before' // 'before' or 'after'
    } = attributes;

    /**
     * Handle Block Unique Id
     */
    useEffect(() => {
        handleUniqueId({ uniqueId, setAttributes, clientId });
        if (items.length === 0) {
            setAttributes({
                items: [
                    {
                        id: '1',
                        question: 'Question 1',
                        answer: 'Answer 1'
                    },
                    {
                        id: '2',
                        question: 'Question 2',
                        answer: 'Answer 2'
                    }
                ]
            });
        }
    }, []);

    const toggleAccordion = index => {
        setAttributes({
            active: active === index ? null : index
        });
    };

    /**
     * Block Props
     */
    const blockProps = useBlockProps({
        className: uniqueId
    });

    return (
        <Fragment>
            {isSelected && <Inspector {...props} />}
            <DynamicStyle {...props} />

            <div {...blockProps}>
                <div className="asfa__container">
                    {items &&
                        items?.length > 0 &&
                        items.map((item, index) => {
                            const isActive = active === index;
                            const prefix = enableAutoNumbering ? getPrefix(index, numberingType) : '';

                            return (
                                <div
                                    className={classNames('single-faq', {
                                        asfa__active_accordion: isActive,
                                        'has-auto-numbering': enableAutoNumbering
                                    })}
                                    key={index}
                                >
                                    <div
                                        className={classNames('asfa__head', {
                                            asfa__head_reverse: iconPosition
                                        })}
                                        role="button"
                                        aria-expanded={isActive}
                                        onClick={() => toggleAccordion(index)}
                                    >
                                        <div
                                            className={classNames('asfa__heading_txt', {
                                                'has-prefix': enablePrefix,
                                                'has-numbering': enableAutoNumbering,
                                                [`numbering-${numberingPosition}`]: enableAutoNumbering
                                            })}
                                        >
                                            {enableAutoNumbering && numberingPosition === 'before' && (
                                                <span className="asfa__numbering">{prefix}</span>
                                            )}
                                            <RichText
                                                tagName={titleTag}
                                                className={'asfa__heading_tag'}
                                                value={item?.question}
                                                onChange={v => {
                                                    const newItems = [...items];
                                                    newItems[index].question = v;
                                                    setAttributes({ items: newItems });
                                                }}
                                                placeholder={__('question..', 'accordion')}
                                            />
                                            {enableAutoNumbering && numberingPosition === 'after' && (
                                                <span className="asfa__numbering">{prefix}</span>
                                            )}
                                        </div>
                                        <div
                                            className={classNames('asfa__icon', {
                                                asfa__active_icon: isActive
                                            })}
                                        >
                                            <div className="faqmate__icon">
                                                {sourceType === 'library' ? ICONS[icon] : <RawHTML>{customIcon}</RawHTML>}
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={classNames('asfa__body', {
                                            asfa__active: isActive
                                        })}
                                        style={{ display: isActive ? 'block' : 'none' }}
                                    >
                                        <div
                                            className={classNames('asfa__body_inner', {
                                                'has-prefix': enablePrefix
                                            })}
                                        >
                                            <RichText
                                                tagName="div"
                                                className="answer"
                                                value={item?.answer}
                                                onChange={v => {
                                                    const newItems = [...items];
                                                    newItems[index].answer = v;
                                                    setAttributes({ items: newItems });
                                                }}
                                                placeholder={__('answer..', 'accordion')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
                <button
                    className="faq-append-btn"
                    onClick={() => {
                        const newItems = [...items];
                        newItems.push({
                            id: newItems.length.toString(),
                            question: 'New Question',
                            answer: 'New Answer'
                        });
                        setAttributes({
                            items: newItems
                        });
                    }}
                >
                    {__('Add Accordion', 'accordion')}
                </button>
            </div>
        </Fragment>
    );
};

export default Edit;
